import * as React from "react"
import { AlertTriangle, Check, Loader2, MoreHorizontal, Pause, Play, RefreshCw, ScanEye, Zap } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useBookingT } from "@/hooks/use-translations"
import { formatUserDateTime } from "@/lib/date-time"
import { cn } from "@/lib/utils"
import { OAUTH_PROVIDERS, type HrisConnection, type HrisRun, type RealtimeOAuthClient } from "@/features/integrations/hris/hrisApi"
import { HrisLuccaRealtimeForm } from "./HrisLuccaRealtimeForm"

type Props = {
  connection: HrisConnection
  run: HrisRun | null
  hasPreview: boolean
  legalComplete: boolean
  busy: boolean
  onPreview: () => void
  onSync: () => void
  onActivate: () => void
  onPause: () => void
  onOpenLegal: () => void
  onScrollToMapping: () => void
  onReplaceKey: () => void
  onDisconnect: () => void
  attentionCount: number
  heldArchives: number
  onOpenAttention: () => void
  /** Resolves true when real-time updates came on. */
  onEnableRealtime: (oauthClient?: RealtimeOAuthClient) => Promise<boolean>
}

/** "03:40" UTC → the viewer's local clock time. */
function localTime(utc: string): string {
  const [h, m] = utc.split(":").map(Number)
  const d = new Date(Date.UTC(2000, 0, 1, h, m))
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
}

export function StatusBadge({ connection }: { connection: HrisConnection }) {
  const t = useBookingT()
  switch (connection.status) {
    case "active":
      return <Badge variant="green">{t("hris_status_active", "Syncing nightly")}</Badge>
    case "paused":
      return <Badge variant="frozen">{t("hris_status_paused", "Paused")}</Badge>
    case "error":
      return <Badge variant="destructive">{t("hris_status_error", "Last sync failed")}</Badge>
    default:
      return <Badge variant="warning">{t("hris_status_draft", "Not switched on yet")}</Badge>
  }
}

/**
 * Status, the steps left before the sync can be switched on, and the actions.
 * Every unfinished step carries the button that finishes it.
 */
export function HrisStatusCard(props: Props) {
  const { connection, run, hasPreview, legalComplete, busy } = props
  const t = useBookingT()
  const running = run?.status === "running"
  const mapped = Object.keys(connection.settings.legalEntityMap).length > 0 || connection.defaultCompany !== null
  const isActive = connection.status === "active" || connection.status === "error"

  const steps = [
    {
      done: mapped,
      label: t("hris_step_map", "Match the legal entities to your companies"),
      action: { label: t("hris_step_map_btn", "Match"), onClick: props.onScrollToMapping },
    },
    {
      done: legalComplete,
      label: t("hris_step_legal", "Accept Schedule E and allow receiving employee data"),
      action: { label: t("hris_step_legal_btn", "Review and accept"), onClick: props.onOpenLegal },
    },
    {
      done: hasPreview,
      label: t("hris_step_preview", "Review what the sync will change"),
      action: { label: t("hris_step_preview_btn", "Preview"), onClick: props.onPreview, disabled: !mapped || !legalComplete || running },
    },
  ]
  const ready = steps.every((s) => s.done)

  return (
    <Card>
      <CardContent className="space-y-5 p-6">
        <div className="flex items-start justify-between gap-3 max-md:flex-col">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-neutral-900">{t("hris_connection_title", "Connection")}</h2>
              <StatusBadge connection={connection} />
            </div>
            <p className="text-sm text-neutral-800">{connection.tenantUrl}</p>
            <p className="text-sm text-neutral-800">
              {connection.lastSyncAt
                ? t("hris_last_sync", "Last sync: {date}").replace("{date}", formatUserDateTime(connection.lastSyncAt))
                : t("hris_never_synced", "Not synced yet")}
            </p>
            {connection.lastError && <p className="text-sm text-error-700">{connection.lastError}</p>}
            <UpdatesLine connection={connection} onEnableRealtime={props.onEnableRealtime} busy={busy} />
          </div>

          <div className="flex shrink-0 items-center gap-2 max-md:flex-wrap">
            <Button variant="outline" onClick={props.onPreview} disabled={running || busy || !mapped || !legalComplete}>
              {running && run?.kind === "preview" ? <Loader2 className="size-4 animate-spin" /> : <ScanEye className="size-4" />}
              {t("hris_btn_preview", "Preview")}
            </Button>
            {isActive ? (
              <Button onClick={props.onSync} disabled={running || busy}>
                {running && run?.kind === "sync" ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                {t("hris_btn_sync", "Sync now")}
              </Button>
            ) : (
              <Button onClick={props.onActivate} disabled={!ready || busy}>
                <Play className="size-4" />
                {t("hris_btn_activate", "Switch on")}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" aria-label={t("hris_more", "More")}>
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isActive && (
                  <DropdownMenuItem onClick={props.onPause}>
                    <Pause className="size-4" />
                    {t("hris_btn_pause", "Pause the sync")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={props.onReplaceKey}>
                  {OAUTH_PROVIDERS.includes(connection.provider)
                    ? t("hris_btn_reconnect", "Connect again")
                    : t("hris_btn_replace_key", "Replace the API key")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={props.onDisconnect} className="text-error-700">
                  {t("hris_btn_disconnect", "Disconnect")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {props.attentionCount > 0 && (
          <div className="flex items-start justify-between gap-3 rounded-md border border-warning-300 bg-warning-50 p-4 max-md:flex-col">
            <span className="flex items-start gap-2 text-sm text-neutral-900">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning-700" />
              <span>
                {props.attentionCount === 1
                  ? t("hris_attention_banner_one", "1 employee needs your attention: they could not be synced on their own.")
                  : t("hris_attention_banner", "{count} employees need your attention: they could not be synced on their own.").replace(
                      "{count}",
                      String(props.attentionCount),
                    )}
                {props.heldArchives > 0 && (
                  <>
                    {" "}
                    <b>
                      {t("hris_attention_held", "{count} of them are leavers held until you confirm the archive.").replace(
                        "{count}",
                        String(props.heldArchives),
                      )}
                    </b>
                  </>
                )}
              </span>
            </span>
            <Button variant="outline" onClick={props.onOpenAttention}>
              {t("hris_attention_review", "Review")}
            </Button>
          </div>
        )}

        {!isActive && (
          <ol className="space-y-2 border-t border-neutral-200 pt-4">
            {steps.map((step, i) => (
              <li key={i} className="flex items-center justify-between gap-3 max-md:flex-col max-md:items-start">
                <span className="flex items-center gap-2.5 text-sm text-neutral-900">
                  <span
                    className={cn(
                      "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                      step.done ? "bg-success-200 text-success-700" : "bg-neutral-200 text-neutral-800",
                    )}
                  >
                    {step.done ? <Check className="size-3.5" /> : i + 1}
                  </span>
                  {step.label}
                </span>
                {!step.done && (
                  <Button variant="link" className="h-auto p-0" onClick={step.action.onClick} disabled={step.action.disabled}>
                    {step.action.label}
                  </Button>
                )}
              </li>
            ))}
            <li className="flex items-center gap-2.5 text-sm text-neutral-900">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-xs font-semibold text-neutral-800">
                4
              </span>
              {t("hris_step_activate", "Switch the sync on: it then runs every night")}
            </li>
          </ol>
        )}
      </CardContent>
    </Card>
  )
}

function UpdatesLine({
  connection,
  onEnableRealtime,
  busy,
}: {
  connection: HrisConnection
  onEnableRealtime: (oauthClient?: RealtimeOAuthClient) => Promise<boolean>
  busy: boolean
}) {
  const t = useBookingT()
  const [askingClient, setAskingClient] = React.useState(false)
  const nightly = localTime(connection.updates.nightlyAtUtc)

  if (connection.updates.mode === "realtime") {
    return (
      <p className="flex items-center gap-1.5 text-sm text-neutral-800">
        <Zap className="size-4 text-success-600" />
        {t("hris_updates_realtime", "Changes arrive within minutes, plus a full check every night at {time}.").replace("{time}", nightly)}
      </p>
    )
  }

  // Lucca: webhooks need an OAuth client; until one is stored, the button asks for it.
  const needsClient = connection.provider === "lucca" && !connection.updates.oauthClient
  const enable = () => (needsClient ? setAskingClient(true) : void onEnableRealtime())

  return (
    <div className="space-y-1">
      <p className="text-sm text-neutral-800">
        {t("hris_updates_nightly", "Changes are synced once a night, at {time}.").replace("{time}", nightly)}
      </p>
      {connection.updates.webhookStatus === "unavailable" && (
        <p className="text-sm text-neutral-800">
          {t("hris_updates_unavailable", "Real-time updates are off: {reason}").replace("{reason}", connection.updates.webhookError ?? "")}
        </p>
      )}
      {connection.status !== "draft" && !OAUTH_PROVIDERS.includes(connection.provider) && !askingClient && (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <Button variant="link" className="h-auto p-0" onClick={enable} disabled={busy}>
            {connection.updates.webhookStatus === "unavailable"
              ? t("hris_updates_retry", "Try real-time updates again")
              : t("hris_updates_enable", "Turn on real-time updates")}
          </Button>
          {connection.provider === "lucca" && connection.updates.oauthClient && (
            <Button variant="link" className="h-auto p-0" onClick={() => setAskingClient(true)} disabled={busy}>
              {t("hris_rt_replace", "Use another OAuth client")}
            </Button>
          )}
        </div>
      )}
      {askingClient && (
        <HrisLuccaRealtimeForm
          tenantUrl={connection.tenantUrl}
          busy={busy}
          onSubmit={async (client) => {
            const on = await onEnableRealtime(client)
            if (on) setAskingClient(false)
            return on
          }}
          onCancel={() => setAskingClient(false)}
        />
      )}
    </div>
  )
}
